/**
 * Unformat an identifier ONLY when it is a genuine UWI. Entity names (Future
 * Location sticks, well names) are not UWIs, and toUnformatted() uppercases and
 * strips separators, corrupting the name so the server's entity-name lookup can
 * never match it (EVA-6890). Every caller that hands user-pasted identifiers to
 * the filter service must go through this, not through toUnformatted directly.
 */
// Lazy rather than module-level because Uwi is declared below this function.
let sharedParser;

export function toUnformattedIfUwi(id) {
  if (!sharedParser) sharedParser = new Uwi();
  const parser = sharedParser;
  try {
    return parser.isUwi(id) ? parser.toUnformatted(id) : id;
  } catch {
    return id;
  }
}

export default class Uwi {
  constructor() {
    this.name = "Uwi";
  }

  toUnformatted(s) {
    const removedFormatting = this.replaceAll(this.replaceAll(s, "-", ""), "/", "");
    const uwi = this.parse(removedFormatting.toUpperCase());
    return uwi.toUnformatted();
  }
  toFormatted(s) {
    const removedFormatting = this.replaceAll(this.replaceAll(s, "-", ""), "/", "");
    const uwi = this.parse(removedFormatting);
    return uwi.toFormatted();
  }
  toAPI10(s) {
    const removedFormatting = this.replaceAll(this.replaceAll(s, "-", ""), "/", "");
    const uwi = this.parse(removedFormatting);
    return uwi.toAPI10 ? uwi.toAPI10() : uwi.toFormatted();
  }
  toMosaic(s) {
    const removedFormatting = this.replaceAll(this.replaceAll(s, "-", ""), "/", "");
    const uwi = this.parse(removedFormatting);
    return uwi.toMosaic ? uwi.toMosaic() : uwi.toFormatted();
  }

  // True when `s` parses as a recognised UWI (DLS / NTS / US API). Future
  // Location (stick) entity names are NOT UWIs — callers use this to avoid
  // running them through toUnformatted(), which uppercases and strips separators
  // and would corrupt the name so the backend can't match it (EVA-6890).
  isUwi(s) {
    const removedFormatting = this.replaceAll(this.replaceAll(s, "-", ""), "/", "");
    return this.parse(removedFormatting.toUpperCase()).id !== "N/A";
  }

  parse(s) {
    const dls = this.parseDls(s);
    if (dls.id) return dls;
    const nts = this.parseNts(s);
    if (nts.id) return nts;
    const api = this.parseUsApi(s);
    if (api.id) return api;
    return new Unknown("N/A", s);
  }
  escapeRegExp(str) {
    return str.replace(/([.*+?^=!:${}()|\]\\])/g, "\\$1");
  }
  replaceAll(str, find, replace) {
    return str.replace(new RegExp(this.escapeRegExp(find), "g"), replace);
  }
  parseDls(s) {
    const re =
      /^(1?)([0-9A-Z]{2})([0-9|X]{2})([0-9|X]{2})([0-9]{3})([0-9]{2})(W{1})([1-6]{1})([0-9|X]{0,2})$/g;
    const matches = [...s.matchAll(re)];
    if (matches.length === 0) return {};

    return new DLS(
      1,
      matches[0][2],
      matches[0][3],
      matches[0][4],
      matches[0][5],
      matches[0][6],
      matches[0][8],
      matches[0][9].padStart(2, "0")
    );
  }

  parseNts(s) {
    const re =
      /^(2?)([0-9A-Z]{2})([A-Z]{1})([0-9|X]{3})([A-Z]{1})([0-9]{3})([A-Z]{1})([0-9]{2})([0-9|X]{0,2})$/g;
    const matches = [...s.matchAll(re)];
    if (matches.length === 0) return {};

    const event = matches[0][9].padStart(2, "0");
    return new NTS(
      2,
      matches[0][2],
      matches[0][3],
      matches[0][4],
      matches[0][5],
      matches[0][6],
      matches[0][7],
      matches[0][8],
      event
    );
  }
  parseUsApi(s) {
    // Sidetrack is optional to support API10 (10-digit) input in addition to API12/API14
    const re = /^5?([0-9]{2})([0-9]{3})([0-9]{5})([0-9]{2})?([0-9]{0,2})$/g;
    const matches = [...s.matchAll(re)];
    if (matches.length === 0) return {};
    const sidetrack = matches[0][4];
    return new UsAPI(
      5,
      matches[0][1],
      matches[0][2],
      matches[0][3],
      sidetrack ?? "00", // undefined when API10 input — default to zero sidetrack
      sidetrack !== undefined ? matches[0][5] : "00" // default event only for API10 input
    );
  }
}

class DLS {
  constructor(id, loc, lsd, sec, twp, range, mer, event) {
    this.id = id;
    this.loc = loc;
    this.lsd = lsd;
    this.sec = sec;
    this.twp = twp;
    this.range = range;
    this.mer = mer;
    this.event = event;
  }

  toUnformatted() {
    return `${this.id}${this.loc}${this.lsd}${this.sec}${this.twp}${this.range}W${this.mer}${this.event}`;
  }
  toFormatted() {
    return `${this.id}${this.loc}/${this.lsd}-${this.sec}-${this.twp}-${this.range}W${this.mer}/${this.event}`;
  }
  toAPI10() {
    // DLS is Canadian format, not applicable to API10 (US format)
    // Return unformatted for non-US wells
    return this.toUnformatted();
  }
  toMosaic() {
    // Mosaic format for Canadian DLS wells: LOC/LSD-SEC-TWP-RGE-MER/EVENT (without ID prefix)
    // Example from 102/03-24-010-26W1/0 → 02/03-24-010-26W1/0 (removes the "1" ID prefix only)
    // Event: Remove leading zeros for display (00 → 0, 02 → 2)
    const eventDisplay = parseInt(this.event, 10).toString();
    return `${this.loc}/${this.lsd}-${this.sec}-${this.twp}-${this.range}W${this.mer}/${eventDisplay}`;
  }
}

class NTS {
  constructor(id, loc, qtr, unit, block, series, area, sheet, event) {
    this.id = id;
    this.loc = loc;
    this.qtr = qtr;
    this.unit = unit;
    this.block = block;
    this.series = series;
    this.area = area;
    this.sheet = sheet;
    this.event = event;
  }

  toUnformatted() {
    return `${this.id}${this.loc}${this.qtr}${this.unit}${this.block}${this.series}${this.area}${this.sheet}${this.event}`;
  }
  toFormatted() {
    return `${this.id}${this.loc}/${this.qtr}-${this.unit}-${this.block}/${this.series}-${this.area}-${this.sheet}/${this.event}`;
  }
  toAPI10() {
    // NTS is Canadian format, not applicable to API10 (US format)
    // Return unformatted for non-US wells
    return this.toUnformatted();
  }
  toMosaic() {
    // Mosaic format for NTS: LOC/QTR-UNIT-BLOCK/SERIES-AREA-SHEET/EVENT (without ID prefix)
    // Event: Remove leading zeros for display (00 → 0, 02 → 2)
    const eventDisplay = parseInt(this.event, 10).toString();
    return `${this.loc}/${this.qtr}-${this.unit}-${this.block}/${this.series}-${this.area}-${this.sheet}/${eventDisplay}`;
  }
}

class UsAPI {
  constructor(id, state, county, uwi, sidetrack, event) {
    this.id = id;
    this.state = state;
    this.county = county;
    this.uwi = uwi;
    this.sidetrack = sidetrack;
    this.event = event;
  }
  toUnformatted() {
    return `${this.state}${this.county}${this.uwi}${this.sidetrack}${this.event}`;
  }
  toFormatted() {
    return `${this.state}-${this.county}-${this.uwi}-${this.sidetrack}-${this.event}`;
  }
  toAPI10() {
    // API10 is the first 10 digits: State (2) + County (3) + Serial (5)
    return `${this.state}${this.county}${this.uwi.substring(0, 5)}`;
  }
  toMosaic() {
    // Mosaic for US wells uses standard formatted API
    return this.toFormatted();
  }
}

class Unknown {
  constructor(id, uwi) {
    this.id = id;
    this.uwi = uwi;
  }
  toUnformatted() {
    return `${this.uwi}`;
  }
  toFormatted() {
    return `${this.uwi}`;
  }
  toAPI10() {
    return `${this.uwi}`;
  }
  toMosaic() {
    return `${this.uwi}`;
  }
}
